import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  ListGroup,
  ListGroupItem,
  Badge,
  Button,
} from "reactstrap";

import apiService from 'service/apiService';
import apiConfig, { buildEndpointUrl } from 'config/apiConfig';
import AddOwner from '../../owners/AddOwner';
import { AddressFormModal, ContactFormModal } from '../shared';
import NotesPanel from '../../../../components/Notes/NotesPanel';

const MerchantOwnersTab = ({ merchant }) => {
  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [ownerContacts, setOwnerContacts] = useState({}); // Store contacts for each owner
  const [loadingContacts, setLoadingContacts] = useState({}); // Track loading state for each owner
  const [ownerAddresses, setOwnerAddresses] = useState({}); // Store addresses for each owner
  const [loadingAddresses, setLoadingAddresses] = useState({}); // Track loading state for each owner
  
  // States for modals
  const [showAddOwnerModal, setShowAddOwnerModal] = useState(false);
  const [showAddOwnerAddressModal, setShowAddOwnerAddressModal] = useState(false);
  const [showAddOwnerContactModal, setShowAddOwnerContactModal] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Function to load owners for the merchant
  const loadOwners = async () => {
    if (!merchant || !merchant.id) return;
    
    setLoadingOwners(true);
    try {
      // ✅ Usar apiService con buildEndpointUrl para la URL correcta
      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.owners(merchant.id));
      const data = await apiService.get(endpoint);
      
      console.log('🔍 DEBUG - Owners API Response:', data);
      console.log('🔍 DEBUG - Response type:', typeof data);
      console.log('🔍 DEBUG - Is array:', Array.isArray(data));
      console.log('🔍 DEBUG - First owner data:', data && data.length > 0 ? data[0] : 'No owners');
      console.log('🔍 DEBUG - Owner keys:', data && data.length > 0 ? Object.keys(data[0]) : 'No owners');
      
      // Handle different response structures
      let ownersData = data;
      if (data && data.content && Array.isArray(data.content)) {
        // If response is paginated
        ownersData = data.content;
      } else if (data && Array.isArray(data)) {
        // If response is direct array with nested structure
        ownersData = data.map(item => {
          // Extract owner data and flatten the structure
          const ownerData = item.owner || item;
          return {
            ...ownerData,
            id: ownerData.id,
            // Include contacts and addresses if they exist
            contacts: item.contacts || [],
            addresses: item.addresses || []
          };
        });
      } else if (data && data.owners && Array.isArray(data.owners)) {
        // If response has owners property
        ownersData = data.owners;
      } else {
        // Fallback
        ownersData = [];
      }
      
      console.log('🔍 DEBUG - Processed owners data:', ownersData);
      console.log('🔍 DEBUG - First owner details:', ownersData.length > 0 ? ownersData[0] : 'No owners');
      setOwners(ownersData);
    } catch (error) {
      console.error('Error loading owners:', error);
      setOwners([]);
    } finally {
      setLoadingOwners(false);
    }
  };

  // Function to load contacts for a specific owner
  const loadOwnerContacts = async (ownerId) => {
    if (!ownerId) return;
    
    setLoadingContacts(prev => ({ ...prev, [ownerId]: true }));
    try {
             // ✅ Usar apiService con buildEndpointUrl para la URL correcta
       const endpoint = buildEndpointUrl('OWNERS', apiConfig.ENDPOINTS.OWNERS.contacts(merchant.id, ownerId));
       const data = await apiService.get(endpoint);
      
      console.log('🔍 DEBUG - Owner Contacts API Response for owner', ownerId, ':', data);
      
      // Handle different response structures
      let contactsData = data;
      if (data && data.content && Array.isArray(data.content)) {
        // If response is paginated
        contactsData = data.content;
      } else if (data && Array.isArray(data)) {
        // If response is direct array
        contactsData = data;
      } else if (data && data.contacts && Array.isArray(data.contacts)) {
        // If response has contacts property
        contactsData = data.contacts;
      } else {
        // Fallback
        contactsData = [];
      }
      
      console.log('🔍 DEBUG - Processed contacts data for owner', ownerId, ':', contactsData);
      setOwnerContacts(prev => ({ ...prev, [ownerId]: contactsData }));
    } catch (error) {
      console.error('Error loading contacts for owner', ownerId, ':', error);
      setOwnerContacts(prev => ({ ...prev, [ownerId]: [] }));
    } finally {
      setLoadingContacts(prev => ({ ...prev, [ownerId]: false }));
    }
  };

  // Function to load addresses for a specific owner
  const loadOwnerAddresses = async (ownerId) => {
    if (!ownerId) return;
    
    setLoadingAddresses(prev => ({ ...prev, [ownerId]: true }));
    try {
             // ✅ Usar apiService con buildEndpointUrl para la URL correcta
       const endpoint = buildEndpointUrl('OWNERS', apiConfig.ENDPOINTS.OWNERS.addresses(merchant.id, ownerId));
       const data = await apiService.get(endpoint);
      
      console.log('🔍 DEBUG - Owner Addresses API Response for owner', ownerId, ':', data);
      
      // Handle different response structures
      let addressesData = data;
      if (data && data.content && Array.isArray(data.content)) {
        // If response is paginated
        addressesData = data.content;
      } else if (data && Array.isArray(data)) {
        // If response is direct array
        addressesData = data;
      } else if (data && data.addresses && Array.isArray(data.addresses)) {
        // If response has addresses property
        addressesData = data.addresses;
      } else {
        // Fallback
        addressesData = [];
      }
      
      console.log('🔍 DEBUG - Processed addresses data for owner', ownerId, ':', addressesData);
      setOwnerAddresses(prev => ({ ...prev, [ownerId]: addressesData }));
    } catch (error) {
      console.error('Error loading addresses for owner', ownerId, ':', error);
      setOwnerAddresses(prev => ({ ...prev, [ownerId]: [] }));
    } finally {
      setLoadingAddresses(prev => ({ ...prev, [ownerId]: false }));
    }
  };

  // Load owners when component mounts or merchant changes
  useEffect(() => {
    loadOwners();
  }, [merchant]);

  // Load contacts and addresses for each owner when owners change
  useEffect(() => {
    if (owners.length > 0) {
      owners.forEach(owner => {
        if (owner.id) {
          loadOwnerContacts(owner.id);
          loadOwnerAddresses(owner.id);
        }
      });
    }
  }, [owners]);

  // Modal handler functions
  const handleAddOwner = () => {
    setShowAddOwnerModal(true);
  };

  const handleAddOwnerAddress = (ownerId) => {
    setSelectedOwnerId(ownerId);
    setShowAddOwnerAddressModal(true);
  };

  const handleAddOwnerContact = (ownerId) => {
    setSelectedOwnerId(ownerId);
    setShowAddOwnerContactModal(true);
  };

  const toggleAddOwnerModal = () => {
    setShowAddOwnerModal(!showAddOwnerModal);
  };

  const toggleAddOwnerAddressModal = () => {
    setShowAddOwnerAddressModal(!showAddOwnerAddressModal);
  };

  const toggleAddOwnerContactModal = () => {
    setShowAddOwnerContactModal(!showAddOwnerContactModal);
  };

  // Callback function to reload contacts after adding a new one
  const handleContactAdded = async () => {
    if (selectedOwnerId) {
      await loadOwnerContacts(selectedOwnerId);
    }
  };

  // Callback function to reload addresses after adding a new one
  const handleAddressAdded = async () => {
    if (selectedOwnerId) {
      await loadOwnerAddresses(selectedOwnerId);
    }
  };

  // Function to get available address types for a specific owner
  // Now allows multiple addresses of the same type
  const getAvailableAddressTypes = (ownerId) => {
    return ['PHYSICAL', 'MAILING', 'BILLING'];
  };

  return (
      <Row>
        <Col md="12">
        <style>
          {`
            @media (max-width: 767.98px) {
              .card-body {
                padding: 0.75rem !important;
              }
              .btn {
                min-height: 36px !important;
                font-size: 0.8rem !important;
                padding: 0.4rem 0.6rem !important;
              }
              .table {
                font-size: 0.875rem !important;
              }
              
              /* Header responsivo */
              .card-header .d-flex {
                flex-direction: column !important;
                align-items: flex-start !important;
              }
              .card-header .d-flex > div:last-child {
                margin-top: 0.5rem !important;
                width: 100% !important;
              }
              
              /* Botón Add Owner responsivo */
              .btn-primary {
                width: auto !important;
                min-width: 100px !important;
              }
              
              /* Cards de owners responsivos */
              .card.border {
                margin-bottom: 1rem !important;
              }
              
              .card.border .card-header {
                padding: 0.75rem !important;
              }
              
              .card.border .card-body {
                padding: 0.75rem !important;
              }
              
              .card.border .card-header .d-flex {
                flex-direction: column !important;
                align-items: flex-start !important;
              }
              
              .card.border .card-header .d-flex > div:last-child {
                margin-top: 0.75rem !important;
                width: 100% !important;
              }
              
              .card.border .card-header .btn-group {
                width: 100% !important;
                flex-direction: column !important;
              }
              
              .card.border .card-header .btn-group .btn {
                width: 100% !important;
                margin-bottom: 0.5rem !important;
                margin-right: 0 !important;
              }
              
              .card.border .card-header .btn-group .btn:last-child {
                margin-bottom: 0 !important;
              }
              
              /* Layout de información responsivo */
              .col-md-6 {
                width: 100% !important;
                margin-bottom: 1rem !important;
              }
              
              .col-md-6:last-child {
                margin-bottom: 0 !important;
              }
              
              /* Badges responsivos */
              .badge {
                font-size: 0.7rem !important;
                margin-bottom: 0.25rem !important;
              }
              
              /* Botones de grupo responsivos */
              .btn-group {
                width: 100% !important;
                flex-direction: column !important;
              }
              
              .btn-group .btn {
                width: auto !important;
                min-width: 120px !important;
              margin-bottom: 0.5rem !important;
              margin-right: 0 !important;
            }
              .btn-group .btn:last-child {
                margin-bottom: 0 !important;
              }
              
              /* Row responsivo */
              .row {
                margin: 0 !important;
              }
              .row .col-md-5,
              .row .col-md-3,
              .row .col-md-4 {
                width: 100% !important;
                margin-bottom: 1rem !important;
                padding: 0 !important;
              }
            }
            
            /* Mejoras para rango 768px-991px */
            @media (max-width: 991.98px) and (min-width: 768px) {
              .row .col-md-5,
              .row .col-md-3,
              .row .col-md-4 {
                width: 100% !important;
                margin-bottom: 1rem !important;
                padding: 0 !important;
              }
              
              /* Layout responsivo para tablets */
              .col-md-6 {
                width: 100% !important;
                margin-bottom: 1rem !important;
              }
              
              .col-md-6:last-child {
                margin-bottom: 0 !important;
              }
              
              /* Botones responsivos para tablets */
              .btn-group {
                width: 100% !important;
                flex-direction: column !important;
              }
              
              .btn-group .btn {
                width: 100% !important;
                margin-bottom: 0.5rem !important;
                margin-right: 0 !important;
              }
              
              .btn-group .btn:last-child {
                margin-bottom: 0 !important;
              }
              
                          /* Listas responsivas */
            .list-unstyled li {
              margin-bottom: 0.5rem !important;
              padding: 0.5rem !important;
              background-color: #f8f9fa !important;
              border-radius: 0.375rem !important;
            }
            
            /* Reparar corte en móviles */
            .container-fluid {
              padding-left: 0.5rem !important;
              padding-right: 0.5rem !important;
            }
            
            .card {
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            }
            
            @media (max-width: 575.98px) {
              .card-body {
                padding: 0.5rem !important;
              }
              .btn {
                font-size: 0.75rem !important;
                padding: 0.3rem 0.5rem !important;
                min-height: 32px !important;
              }
              
              /* Cards de owners en móviles */
              .card.border .card-header {
                padding: 0.5rem !important;
              }
              
              .card.border .card-body {
                padding: 0.5rem !important;
              }
              
              .card.border .card-header h5 {
                font-size: 0.9rem !important;
              }
              
              .card.border .card-header .btn-group .btn {
                font-size: 0.7rem !important;
                padding: 0.25rem 0.4rem !important;
                min-height: 28px !important;
              }
              
              /* Botón Add Owner en móviles */
              .btn-primary {
                min-width: 90px !important;
                font-size: 0.7rem !important;
              }
              
              /* Layout de información en móviles */
              .col-md-6 {
                margin-bottom: 0.75rem !important;
              }
              
              .col-md-6 p {
                font-size: 0.75rem !important;
                margin-bottom: 0.4rem !important;
              }
              
              /* Badges en móviles */
              .badge {
                font-size: 0.65rem !important;
              }
            }
          `}
        </style>
        <Card className="shadow-sm">
          <CardHeader  style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb' }} 
            className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
              <i className="ni ni-single-02 mr-2"></i>
              Owners ({loadingOwners ? 'Loading...' : owners.length})
          </h5>
            <Button color="primary" size="sm" onClick={handleAddOwner}>
              <i className="ni ni-plus mr-1"></i>
              Add Owner
            </Button>
          </CardHeader>
          <CardBody>
            {loadingOwners ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="text-muted mt-2 mb-0">Loading owners...</p>
              </div>
            ) : owners.length === 0 ? (
              <div className="text-center py-3">
                <i className="ni ni-single-02 text-muted" style={{ fontSize: '2rem' }}></i>
                <p className="text-muted mt-2 mb-0">No owners found</p>
          </div>
            ) : (
                               <Row>
                   {owners.map((owner, index) => (
                     <Col md="12" key={index} className="mb-4">
                       <Card className="border">
                         <CardHeader style={{ backgroundColor: '#e3f2fd', borderColor: '#bbdefb' }} className="py-2">
                           <div className="d-flex align-items-center justify-content-between">
                             <div className="d-flex align-items-center">
                               <i className="ni ni-single-02 text-muted mr-2" />
                               <h5 className="mb-0 font-weight-bold" style={{ fontSize: '1.1rem' }}>
                                 {owner.firstName || 'N/A'} {owner.middleName || ''} {owner.lastName || 'N/A'}
                               </h5>
                             </div>
                             <div className="d-flex align-items-center">
                               <Badge color="secondary" className="badge-pill mr-3">
                                 {owner.ownershipPercentage || 'N/A'}% Ownership
                               </Badge>
                               <div className="btn-group btn-group-sm">
                                <Button 
                                  color="primary" 
                                  size="sm"
                                  className="mr-1"
                                  onClick={() => handleAddOwnerAddress(owner.id)}
                                  title="Add new address"
                                >
                                  <i className="ni ni-pin-3 mr-1"></i>
                                  Add Address
                                </Button>
                                 <Button 
                                   color="primary" 
                                   size="sm"
                                   onClick={() => handleAddOwnerContact(owner.id)}
                                 >
                                   <i className="ni ni-mobile-button mr-1"></i>
                                   Add Communication Method
                                 </Button>
                               </div>
                             </div>
                           </div>
                         </CardHeader>
                         <CardBody className="pt-3 pb-2">
                           <Row>
                             <Col md="6" className="mb-2">
                               <div className="d-flex align-items-center mb-1">
                                 <small className="text-muted mr-2">Gender:</small>
                                 <small className="font-weight-bold">{owner.gender || 'N/A'}</small>
                               </div>
                               <div className="d-flex align-items-center mb-1">
                                 <small className="text-muted mr-2">Birth:</small>
                                 <small className="font-weight-bold">{formatDate(owner.birthDate)}</small>
                               </div>
                               <div className="d-flex align-items-center mb-1">
                                 <small className="text-muted mr-2">Citizenship:</small>
                                 <small className="font-weight-bold">{owner.citizenship || 'N/A'}</small>
                               </div>
                               <div className="d-flex align-items-center">
                                 <small className="text-muted mr-2">SSN:</small>
                                 <small className="font-weight-bold">***-**-{owner.socialSecurityNumber.slice(-4) || 'N/A'}</small>
                               </div>
                             </Col>
                             <Col md="6" className="mb-2">
                               <div className="mb-2">
                                 <small className="text-muted d-block mb-1">Contacts</small>
                                 {loadingContacts[owner.id] ? (
                                   <small className="text-muted"><span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>Loading contacts...</small>
                                 ) : ownerContacts[owner.id] && ownerContacts[owner.id].length > 0 ? (
                                   <ul className="list-unstyled mb-0">
                                     {ownerContacts[owner.id].map((contact, contactIndex) => (
                                       <li key={contactIndex} className="mb-1 d-flex align-items-center">
                                         <small className="text-muted truncate-text">
                                           <i className={`fas fa-${contact.type === 'EMAIL' ? 'envelope' : 'phone'} mr-2`} />
                                           {contact.value}
                                         </small>
                                         {contact.isPrimary && (
                                          <Badge color="secondary" size="sm" className="badge-pill ml-2">Primary</Badge>
                                        )}
                                       </li>
                                     ))}
                                   </ul>
                                 ) : (
                                   <small className="text-muted">No contact information</small>
                                 )}
                               </div>
                             </Col>
                           </Row>
                           {ownerAddresses[owner.id] && ownerAddresses[owner.id].length > 0 && (
                             <div className="mt-2">
                               <small className="text-muted d-block mb-2">Addresses</small>
                               <Row>
                                 {ownerAddresses[owner.id].map((addr, addrIndex) => (
                                   <Col md="6" key={addrIndex} className="mb-2">
                                     <div className="p-3 border rounded shadow-sm">
                                       <div className="d-flex align-items-center mb-1">
                                         <Badge color="" size="sm" className="text-dark mr-2">{addr.addressType}</Badge>
                                         <small className="font-weight-bold">{addr.city}, {addr.state}</small>
                                       </div>
                                       <small className="d-block">{addr.street1}</small>
                                       {addr.street2 && <small className="d-block text-muted">{addr.street2}</small>}
                                       <small className="text-muted">{addr.zipCode} • {addr.country}</small>
                                     </div>
                                   </Col>
                                 ))}
                               </Row>
                             </div>
                           )}
                           
                           {/* Owner Notes Section */}
                           <div className="mt-3">
                             <NotesPanel
                               tag="owner-notes"
                               originType="OWNER"
                               originId={owner.id}
                               title="Owner Notes"
                               systemTags={["MERCHANT", "OWNER"]}
                               onNoteAdded={(note) => console.log('Owner note added:', note)}
                               onNoteUpdated={(noteId, newMessage) => console.log('Owner note updated:', noteId, newMessage)}
                               onNoteDeleted={(noteId) => console.log('Owner note deleted:', noteId)}
                             />
                           </div>
                         </CardBody>
                       </Card>
                     </Col>
                   ))}
                 </Row>
            )}
          </CardBody>
                 </Card>
      </Col>

      {/* Modals */}
      <AddOwner
        isOpen={showAddOwnerModal}
        toggle={toggleAddOwnerModal}
        merchantId={merchant?.id}
        onOwnerAdded={loadOwners}
      />

      <AddressFormModal
        isOpen={showAddOwnerAddressModal}
        toggle={toggleAddOwnerAddressModal}
        onSave={(addressData) => {
          // Reload addresses after adding
          if (selectedOwnerId) {
            loadOwnerAddresses(selectedOwnerId);
          }
          setShowAddOwnerAddressModal(false);
        }}
        availableAddressTypes={selectedOwnerId ? getAvailableAddressTypes(selectedOwnerId) : ['PHYSICAL', 'MAILING', 'BILLING']}
        showPhoneNumber={false}
        title="Add Owner Address"
        apiEndpoint={selectedOwnerId ? buildEndpointUrl('OWNERS', apiConfig.ENDPOINTS.OWNERS.addresses(merchant.id, selectedOwnerId)) : null}
        apiService={apiService}
      />

      <ContactFormModal
        isOpen={showAddOwnerContactModal}
        toggle={toggleAddOwnerContactModal}
        onSave={handleContactAdded}
        existingContacts={selectedOwnerId ? (ownerContacts[selectedOwnerId] || []) : []}
        title="Add Owner Contact"
        apiEndpoint={selectedOwnerId ? `/merchant-service/api/v2/merchants/${merchant.id}/owners/${selectedOwnerId}/contacts` : null}
        apiService={apiService}
      />
    </Row>
  );
};

export default MerchantOwnersTab;
